import { useMutation, useQuery } from '@apollo/client';
import { OptionalString, parseQueryString } from '@sixfold/app-utils';
import {
  Button,
  Input,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalMain,
  Paragraph,
  Radio,
  RadioGroup,
  Stack,
  useNotifications,
} from '@sixfold/common-ui';
import { isNil } from '@sixfold/typed-primitives';
import React, { useCallback, useState } from 'react';

import { createVehiclesMutation } from '../../company/graphql';
import {
  CreateVehiclesMutation as CreateVehiclesMutationType,
  CreateVehiclesMutationVariables,
  PAGINATION_PAGE_SIZE,
  VehicleTrackerByTelematicsIntegrationAndVehicleDetailsQuery,
  VehicleTrackerByTelematicsIntegrationAndVehicleDetailsQueryVariables,
} from '../../lib/graphql';
import { managedVehiclesQuery } from '../../vehicle/graphql';
import { vehicleTrackerByTelematicsIntegrationAndVehicleDetailsQuery } from '../graphql';

type Props = {
  existingVehicle?: {
    license_plate: string;
    remote_id: string;
    tracker_id: string;
  };
  telematicsIntegrationId: string;
  trigger: React.ReactElement | string;
};

export const ManageVehicleModal = (props: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [licensePlate, setLicensePlate] = useState(props.existingVehicle?.license_plate);
  const [remoteId, setRemoteId] = useState(props.existingVehicle?.remote_id);
  const [isEditingRemoteId, setIsEditingRemoteId] = useState(false);
  const [licensePlateQueryResult, setLicensePlateQueryResult] = useState<{ vehicleTrackerId: string } | null>(null);
  const [remoteIdQueryResult, setRemoteIdQueryResult] = useState<{ vehicleTrackerId: string } | null>(null);

  const isInvalidRemoteId = (id: string | undefined) => {
    return id !== undefined && id.trim() !== id;
  };

  const isInvalidLicensePlate = (plate: string | undefined) => {
    return plate !== undefined && plate.trim() !== plate;
  };

  const isDuplicateTracker = useCallback(
    (tracker: { vehicleTrackerId: string } | null) => {
      return tracker !== null && tracker.vehicleTrackerId !== props.existingVehicle?.tracker_id;
    },
    [props.existingVehicle],
  );

  const isProblemWithLicensePlate = isInvalidLicensePlate(licensePlate) || isDuplicateTracker(licensePlateQueryResult);
  const isProblemWithRemoteId = isInvalidRemoteId(remoteId) || isDuplicateTracker(remoteIdQueryResult);

  const isEditing = !isNil(props.existingVehicle);
  const notify = useNotifications();

  const onToggle = () => {
    setLicensePlate(props.existingVehicle?.license_plate);
    setRemoteId(props.existingVehicle?.remote_id);
    setLicensePlateQueryResult(null);
    setRemoteIdQueryResult(null);
    setIsEditingRemoteId(false);
    setIsOpen(!isOpen);
  };

  const queryString = parseQueryString({
    queryParametersToParse: {
      license_plate: OptionalString,
    },
    queryString: location.search,
  });

  const getLicensePlateErrorMsg = (isInvalidLicensePlateError: boolean, isDuplicateLicensePlateError: boolean) => {
    if (isInvalidLicensePlateError) {
      return 'The license plate you are trying to insert is invalid! Check leading or trailing spaces.';
    } else if (isDuplicateLicensePlateError) {
      return 'The license plate you are trying to insert is already used by another vehicle in this integration!';
    }
    return 'The license plate you are trying to insert is invalid!';
  };

  const getRemoteIdErrorMsg = (isInvalidRemoteIdError: boolean, isDuplicateRemoteIdError: boolean) => {
    if (isInvalidRemoteIdError) {
      return 'The remote id you are trying to insert is invalid! Check leading or trailing spaces.';
    } else if (isDuplicateRemoteIdError) {
      return 'The remote id you are trying to insert is already used by another vehicle in this integration!';
    }
    return 'The remote id you are trying to insert is invalid!';
  };

  const [createVehicles] = useMutation<CreateVehiclesMutationType, CreateVehiclesMutationVariables>(
    createVehiclesMutation,
  );

  const licensePlateResult = useQuery<
    VehicleTrackerByTelematicsIntegrationAndVehicleDetailsQuery,
    VehicleTrackerByTelematicsIntegrationAndVehicleDetailsQueryVariables
  >(vehicleTrackerByTelematicsIntegrationAndVehicleDetailsQuery, {
    variables: { telematicsIntegrationId: props.telematicsIntegrationId, licensePlate },
    fetchPolicy: 'no-cache',
    skip: !isOpen,
    onCompleted: (data) => setLicensePlateQueryResult(data.vehicleTrackerByTelematicsIntegrationAndVehicleDetails),
  });

  const remoteIdResult = useQuery<
    VehicleTrackerByTelematicsIntegrationAndVehicleDetailsQuery,
    VehicleTrackerByTelematicsIntegrationAndVehicleDetailsQueryVariables
  >(vehicleTrackerByTelematicsIntegrationAndVehicleDetailsQuery, {
    variables: { telematicsIntegrationId: props.telematicsIntegrationId, remoteId },
    fetchPolicy: 'no-cache',
    skip: !isOpen,
    onCompleted: (data) => setRemoteIdQueryResult(data.vehicleTrackerByTelematicsIntegrationAndVehicleDetails),
  });

  const handleConfirm = async () => {
    if (!licensePlate || !remoteId) {
      return;
    }

    await createVehicles({
      variables: {
        input: {
          vehicles: [
            {
              licensePlate,
              remoteId,
            },
          ],
          telematicsIntegrationId: props.telematicsIntegrationId,
        },
      },
      refetchQueries: [
        {
          query: managedVehiclesQuery,
          variables: {
            integrationIds: [props.telematicsIntegrationId],
            limit: PAGINATION_PAGE_SIZE,
            licensePlateSearch: queryString.license_plate || undefined,
          },
        },
      ],
    });
    notify.success({ title: isEditing ? 'Vehicle updated' : 'Vehicle added' });
    onToggle();
  };

  const licensePlateTracker = licensePlateResult.data?.vehicleTrackerByTelematicsIntegrationAndVehicleDetails ?? null;
  const remoteIdTracker = remoteIdResult.data?.vehicleTrackerByTelematicsIntegrationAndVehicleDetails ?? null;

  const invalidPlate = isInvalidLicensePlate(licensePlate);
  const duplicatePlate = isDuplicateTracker(licensePlateTracker);
  const invalidRemoteId = isInvalidRemoteId(remoteId);
  const duplicateRemoteId = isDuplicateTracker(remoteIdTracker);

  return (
    <Modal trigger={props.trigger} open={isOpen} onToggle={onToggle}>
      <ModalHeader title={isEditing ? 'Edit vehicle' : 'Add vehicle'} />
      <ModalMain>
        {isEditing ? (
          <Stack>
            <Paragraph>You are allowed to edit one of the values at a time.</Paragraph>
            <RadioGroup
              name="editing_choice"
              label="Editing choice"
              labelPosition="hidden"
              onChange={(value) => {
                setIsEditingRemoteId(value.value === 'remote_id');
                if (value.value === 'remote_id') {
                  setLicensePlate(props.existingVehicle?.license_plate);
                } else {
                  setRemoteId(props.existingVehicle?.remote_id);
                }
              }}
              value={isEditingRemoteId ? 'remote_id' : 'licenseplate'}>
              <Stack direction="row" wrap={false}>
                <Radio value="licenseplate" label="" />
                <Input
                  className={'maxWidth'}
                  onChange={(event) => setLicensePlate(event.value)}
                  errorMessage={
                    invalidPlate || duplicatePlate ? getLicensePlateErrorMsg(invalidPlate, duplicatePlate) : undefined
                  }
                  value={licensePlate}
                  disabled={isEditingRemoteId}
                  label="License plate"
                  name="license_plate"
                />
              </Stack>

              <Stack direction="row" wrap={false}>
                <Radio value="remote_id" label="" />
                <Input
                  className={'maxWidth'}
                  errorMessage={
                    invalidRemoteId || duplicateRemoteId
                      ? getRemoteIdErrorMsg(invalidRemoteId, duplicateRemoteId)
                      : undefined
                  }
                  onChange={(event) => setRemoteId(event.value)}
                  value={remoteId}
                  disabled={!isEditingRemoteId}
                  label="Remote ID"
                  name="remote_id"
                />
              </Stack>
            </RadioGroup>
          </Stack>
        ) : (
          <>
            <Input
              onChange={(event) => setLicensePlate(event.value)}
              value={licensePlate}
              errorMessage={
                invalidPlate || duplicatePlate ? getLicensePlateErrorMsg(invalidPlate, duplicatePlate) : undefined
              }
              label="License plate"
              name="license_plate"
            />
            <Input
              onChange={(event) => setRemoteId(event.value)}
              value={remoteId}
              errorMessage={
                invalidRemoteId || duplicateRemoteId
                  ? getRemoteIdErrorMsg(invalidRemoteId, duplicateRemoteId)
                  : undefined
              }
              label="Remote ID"
              name="remote_id"
            />
          </>
        )}
      </ModalMain>
      <ModalFooter>
        <Stack justifyContent="end">
          <Button kind="subtle" onClick={onToggle}>
            Cancel
          </Button>
          <Button
            onClick={() => handleConfirm()}
            disabled={
              isProblemWithLicensePlate ||
              isProblemWithRemoteId ||
              isNil(licensePlate) ||
              isNil(remoteId) ||
              licensePlate === '' ||
              remoteId === ''
            }
            kind="primary">
            {isEditing ? 'Save' : 'Add'}
          </Button>
        </Stack>
      </ModalFooter>
    </Modal>
  );
};
